import { IonIcon } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import React, { useMemo } from "react";
import { PANEL_RESIZE_PROFILES } from "../../../config/itineraryPanel.configuration";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { type ItineraryListContentMode } from "../../../types/ItineraryPlanner";
import {
    itineraryPanelResizeAdapter,
    PanelResizeHandle,
    type PanelResizeLayout,
} from "../panelResize";

/** Uppercase title shown in the list chrome row for each content mode. */
const LIST_CHROME_TITLES: Record<ItineraryListContentMode, string> = {
    "search": "Résultats de recherche",
    "saved":  "Itinéraires sauvegardés",
};

interface ItineraryListChromeRowProps {

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi": boolean;

    /** Shared resize layout measurements from Home. */
    "panelResizeLayout": PanelResizeLayout;

    /** List content mode controlling chrome layout. */
    "contentMode"?: ItineraryListContentMode;

    /** Closes the list panel (saved mode or search results). */
    "onCloseList"?: () => void;
}

/**
 * Top chrome row for the itinerary list (drag handle, mode title, and close control).
 */
const ItineraryListChromeRow: React.FC<ItineraryListChromeRowProps> = ( {
    isDarkUi,
    panelResizeLayout,
    contentMode = "search",
    onCloseList,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const resizeProfile = contentMode === "saved"
        ? PANEL_RESIZE_PROFILES.itineraryListSaved
        : PANEL_RESIZE_PROFILES.itineraryList;
    const chromeRowHeightPx = resizeProfile.chromeRowHeightPx;
    const chromeTitle = LIST_CHROME_TITLES[contentMode];
    const closeIconColor = useMemo( (): string => {
        const iconBackground = isDarkUi ? palette.overlayCardBackgroundStrong : palette.overlayCardBackgroundSoft;

        return `color-mix(in srgb, ${ palette.overlayTextSecondary } 68%, ${ iconBackground })`;
    }, [ isDarkUi, palette.overlayCardBackgroundSoft, palette.overlayCardBackgroundStrong, palette.overlayTextSecondary ] );
    const closeButtonStyle = useMemo( (): React.CSSProperties => ( {
        "borderColor": palette.overlayBorderStrong,
        "background":  isDarkUi ? palette.overlayCardBackgroundStrong : palette.overlayCardBackgroundSoft,
        "color":       closeIconColor,
    } ), [ closeIconColor, isDarkUi, palette.overlayBorderStrong, palette.overlayCardBackgroundSoft, palette.overlayCardBackgroundStrong ] );

    return (
        <div
            className="relative z-10 flex w-full shrink-0 items-center md:rounded-t-2xl"
            id="itinerary-list-chrome-row"
            style={{
                "borderBottomColor": palette.overlayBorder,
                "height":            `${ chromeRowHeightPx }px`,
            }}
        >
            <p
                className="pointer-events-none absolute left-2 max-w-[40%] truncate text-xs font-semibold uppercase tracking-wide"
                style={{ "color": palette.overlayTextSecondary }}
            >
                {chromeTitle}
            </p>
            <div className="flex flex-1 items-center justify-center">
                <PanelResizeHandle
                    adapter={itineraryPanelResizeAdapter}
                    containerRef={panelResizeLayout.containerRef}
                    isDarkUi={isDarkUi}
                    layout="inline"
                    layoutBounds={panelResizeLayout}
                    profile={resizeProfile}
                />
            </div>
            {onCloseList !== undefined
                ? (
                    <button
                        aria-label="Fermer"
                        className="absolute right-[5px] z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-solid backdrop-blur-sm transition-transform duration-150 ease-out active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                        id={contentMode === "saved" ? "saved-list-close-button" : "search-list-close-button"}
                        onClick={onCloseList}
                        style={closeButtonStyle}
                        type="button"
                    >
                        <IonIcon
                            aria-hidden="true"
                            icon={closeOutline}
                            style={{ "fontSize": "18px" }}
                        />
                    </button>
                )
                : null}
        </div>
    );
};

export default ItineraryListChromeRow;
